import { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { icons } from '../../../assets'

// ---------------------------------------------------------------------------
// Filtro de Periodo + Rango de fechas
// ---------------------------------------------------------------------------
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"]
const MESES_ABR = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"]
const DIAS_SEMANA = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"]

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }
const addMonths = (d, n) => { const r = new Date(d); r.setMonth(r.getMonth() + n); return r }

// Lunes como inicio de semana: getDay() es 0=domingo..6=sabado.
const startOfWeek = (d) => { const day = (d.getDay() + 6) % 7; return addDays(d, -day) }
const endOfWeek = (d) => addDays(startOfWeek(d), 6)
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1)

const PERIOD_OPTIONS = [
    { key: "hoy", label: "Hoy", range: () => { const t = startOfDay(new Date()); return [t, t] } },
    { key: "esta_semana", label: "Esta semana", range: () => { const t = startOfDay(new Date()); return [startOfWeek(t), endOfWeek(t)] } },
    { key: "semana_pasada", label: "Semana pasada", range: () => { const t = addDays(startOfDay(new Date()), -7); return [startOfWeek(t), endOfWeek(t)] } },
    { key: "este_mes", label: "Este mes", range: () => { const t = startOfDay(new Date()); return [startOfMonth(t), t] } },
    { key: "mes_anterior", label: "Mes anterior", range: () => { const t = addMonths(startOfDay(new Date()), -1); return [startOfMonth(t), endOfMonth(t)] } },
    { key: "este_anio", label: "Este año", range: () => { const t = startOfDay(new Date()); return [startOfYear(t), t] } },
]

const formatRangeLabel = (start, end) => {
    const sameYear = start.getFullYear() === end.getFullYear()
    const sameMonth = sameYear && start.getMonth() === end.getMonth()
    if (sameMonth) return `${start.getDate()} - ${end.getDate()} ${MESES_ABR[end.getMonth()]}, ${end.getFullYear()}`
    if (sameYear) return `${start.getDate()} ${MESES_ABR[start.getMonth()]} - ${end.getDate()} ${MESES_ABR[end.getMonth()]}, ${end.getFullYear()}`
    return `${start.getDate()} ${MESES_ABR[start.getMonth()]}, ${start.getFullYear()} - ${end.getDate()} ${MESES_ABR[end.getMonth()]}, ${end.getFullYear()}`
}

// YYYY-MM-DD en hora local (el back interpreta esto como el dia completo).
const formatDateParam = (date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
}

// ---------------------------------------------------------------------------
// Agrupación de datos crudos (lecciones/evaluaciones) y KPIs
// ---------------------------------------------------------------------------
// Cada fila de arrayData trae en `evaluaciones` el objeto `responses` crudo del
// documento (una o varias entradas, cada una keyeada por un id de sesión que
// puede repetirse o variar entre intentos). El tipo de cada entrada se detecta
// por sus propios campos (`lessonId` = lección, `evaluationId` = evaluación,
// con sus `sections[].sectionId`), y se agrupa por `title`, que es el único
// campo estable entre sesiones — agrupar por el id de la key rompe el conteo.
// Las lecciones además traen `module` (nombre real del campo en el JSON que manda
// Unity), y un mismo título puede repetirse en módulos distintos, así que para
// lecciones la agrupación es `module + title`.
const ENTRY_TYPE = { LESSON: "lesson", EVALUATION: "evaluation" }

const getEntryType = (entry) => {
    if (!entry) return null
    if (entry.lessonId) return ENTRY_TYPE.LESSON
    if (entry.evaluationId) return ENTRY_TYPE.EVALUATION
    return null
}

const getEntryGroupKey = (type, entry) => (
    type === ENTRY_TYPE.LESSON ? `${entry.module || ""}::${entry.title}` : entry.title
)

const isWithinRange = (dateValue, start, end) => {
    if (!dateValue || !start || !end) return false
    const date = new Date(dateValue)
    return date >= start && date < addDays(end, 1) // end es inclusivo (fin del dia)
}

const countUniqueUsersInRange = (arrayData = [], start, end) => {
    const ids = new Set()
    arrayData.forEach((row) => {
        if (isWithinRange(row.Fecha_y_hora, start, end)) ids.add(row._idUser)
    })
    return ids.size
}

const countUniqueUsersWithEntryTypeInRange = (arrayData = [], start, end, type) => {
    const ids = new Set()
    arrayData.forEach((row) => {
        if (!isWithinRange(row.Fecha_y_hora, start, end)) return
        const entries = row.evaluaciones || {}
        const hasType = Object.values(entries).some((entry) => getEntryType(entry) === type)
        if (hasType) ids.add(row._idUser)
    })
    return ids.size
}

const countUniqueUsersWithLessonCompletedInRange = (arrayData, start, end) => (
    countUniqueUsersWithEntryTypeInRange(arrayData, start, end, ENTRY_TYPE.LESSON)
)

const countUniqueUsersWithEvaluationCompletedInRange = (arrayData, start, end) => (
    countUniqueUsersWithEntryTypeInRange(arrayData, start, end, ENTRY_TYPE.EVALUATION)
)

// Catálogo de lecciones (módulo + tema) tal como está armado del lado de la app VR.
// El título por sí solo no es único ("Primeros pasos" y "Manejo seguro" se repiten
// en más de un módulo), por eso la clave de agrupación es módulo + título.
const LESSON_CATALOG = [
    { module: "Equipo de Protección Personal", title: "Equipo de Protección Personal" },
    { module: "Montacargas Hombre Sentado", title: "Primeros pasos" },
    { module: "Montacargas Hombre Sentado", title: "Uso de rampa" },
    { module: "Montacargas Hombre Sentado", title: "Manejo seguro" },
    { module: "Montacargas Hombre a Pie", title: "Primeros pasos" },
    { module: "Montacargas Hombre a Pie", title: "Manejo seguro" },
    { module: "Montacargas Hombre a Pie", title: "Colocación en rack" },
    { module: "Patín hidráulico", title: "Primeros pasos" },
    { module: "Patín hidráulico", title: "Manejo seguro" },
    { module: "SAP: Logística y manejo", title: "HU Managment, QM & Batch Management" },
    { module: "SAP: Logística y manejo", title: "Proceso: Receiving" },
    { module: "SAP: Logística y manejo", title: "Proceso: Putaway" },
    { module: "SAP: Logística y manejo", title: "Proceso: Picking | MHS" },
    { module: "SAP: Logística y manejo", title: "Proceso: Picking | MHP" },
    { module: "SAP: Logística y manejo", title: "Proceso: Picking | PH" },
    { module: "SAP: Logística y manejo", title: "Proceso: Shipping" },
    { module: "SAP: Logística y manejo", title: "Proceso: Devoluciones" },
    { module: "SAP: Logística y manejo", title: "Proceso: Vendor return" },
    { module: "SAP: Logística y manejo", title: "Proceso: Inventario cíclico" },
]

const countLessonOccurrencesByKey = (arrayData = [], start, end) => {
    const counts = new Map()
    arrayData.forEach((row) => {
        if (!isWithinRange(row.Fecha_y_hora, start, end)) return
        const entries = row.evaluaciones || {}
        Object.values(entries).forEach((entry) => {
            if (getEntryType(entry) !== ENTRY_TYPE.LESSON) return
            const key = getEntryGroupKey(ENTRY_TYPE.LESSON, entry)
            counts.set(key, (counts.get(key) || 0) + 1)
        })
    })
    return counts
}

// Ordenado de mayor a menor cantidad de veces cursada, igual que el mock hardcodeado.
const getLessonsChartData = (arrayData, start, end) => {
    const counts = countLessonOccurrencesByKey(arrayData, start, end)
    return LESSON_CATALOG
        .map(({ module, title }) => {
            const key = getEntryGroupKey(ENTRY_TYPE.LESSON, { module, title })
            return { id: key, label: title, value: counts.get(key) || 0 }
        })
        .sort((a, b) => b.value - a.value)
}

// Catálogo de evaluaciones: `title` es el valor real que trae `title` dentro de cada
// entrada de `responses` (ej. "HS | Receiving", tal cual lo manda Unity), y es la
// clave de agrupación. `label` es el texto que se muestra en la UI — hoy es igual a
// `title`, se dejan separados por si a futuro la UI necesita un texto distinto.
const EVALUATION_CATALOG = [
    { title: "HS | Receiving", label: "HS | Receiving" },
    { title: "HP | Putaway", label: "HP | Putaway" },
    { title: "PH | Picking", label: "PH | Picking" },
]

const getEvaluationLabelByTitle = (title) => (
    EVALUATION_CATALOG.find((e) => e.title === title)?.label || title
)

// entry.sections[].result usa el enum 0=Passed, 1=PassedWithReservations, 2=Failed.
// 0 y 1 cuentan como aprobado; solo 2 (Failed) reprueba la sección.
// Una evaluación está aprobada cuando NINGUNA sección es 2.
const isEvaluationApproved = (entry) => (
    Array.isArray(entry.sections) && entry.sections.length > 0 && entry.sections.every((section) => section.result !== 2)
)

const getEvaluationStatsByTitle = (arrayData = [], start, end) => {
    const stats = new Map() // title -> { totalUserIds: Set, approvedUserIds: Set }
    arrayData.forEach((row) => {
        if (!isWithinRange(row.Fecha_y_hora, start, end)) return
        const entries = row.evaluaciones || {}
        Object.values(entries).forEach((entry) => {
            if (getEntryType(entry) !== ENTRY_TYPE.EVALUATION) return
            if (!stats.has(entry.title)) stats.set(entry.title, { totalUserIds: new Set(), approvedUserIds: new Set() })
            const entryStats = stats.get(entry.title)
            entryStats.totalUserIds.add(row._idUser)
            if (isEvaluationApproved(entry)) entryStats.approvedUserIds.add(row._idUser)
        })
    })
    return stats
}

const getEvaluationSummaryData = (arrayData, start, end) => {
    const stats = getEvaluationStatsByTitle(arrayData, start, end)
    return EVALUATION_CATALOG.map(({ title, label }) => {
        const entryStats = stats.get(title)
        return {
            key: title,
            label,
            totalUsers: entryStats ? entryStats.totalUserIds.size : 0,
            completedUsers: entryStats ? entryStats.approvedUserIds.size : 0,
        }
    })
}

// dd/mm/aa, igual al formato que ya se mostraba (ej. "21/08/26").
const formatShortDate = (date) => {
    const d = String(date.getDate()).padStart(2, "0")
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const y = String(date.getFullYear()).slice(-2)
    return `${d}/${m}/${y}`
}

// El tiempo real por evaluación viene repartido en cada sección (sectionTimeSeconds),
// hay que sumarlas todas — no existe un total ya calculado a nivel de evaluación.
const sumEvaluationSecondsByTitle = (arrayData = [], start, end) => {
    const totals = new Map()
    arrayData.forEach((row) => {
        if (!isWithinRange(row.Fecha_y_hora, start, end)) return
        const entries = row.evaluaciones || {}
        Object.values(entries).forEach((entry) => {
            if (getEntryType(entry) !== ENTRY_TYPE.EVALUATION) return
            const sections = Array.isArray(entry.sections) ? entry.sections : []
            const seconds = sections.reduce((sum, section) => sum + (section.sectionTimeSeconds || 0), 0)
            totals.set(entry.title, (totals.get(entry.title) || 0) + seconds)
        })
    })
    return totals
}

const getEvaluationTimeInvestedData = (arrayData, start, end) => {
    const totals = sumEvaluationSecondsByTitle(arrayData, start, end)
    const dateRangeLabel = `${formatShortDate(start)} - ${formatShortDate(end)}`
    return EVALUATION_CATALOG.map(({ title, label }) => ({
        key: title,
        label,
        dateRange: dateRangeLabel,
        seconds: totals.get(title) || 0,
    }))
}

// Se incluye cualquier step con result 1 (Correct) o 2 (Incorrect); solo se
// excluye result === 3 (NotPerformed=omitido, el step no se llegó a hacer).
// Se agrupa por stepName (no stepId, que se repite) + evaluación. El "tipo
// de error" sale de step.categoryIds: "0"=Proceso operativo, "1"=Prácticas
// seguras. Un step puede traer un solo id o ambos — cuando trae los dos, la
// fila del step lista ambos tipos (cada uno con su propio conteo de
// usuarios) en vez de duplicar la fila completa.
const ERROR_CATEGORY_ID_TO_TYPE = { "0": "proceso_operativo", "1": "practicas_seguras" }
const ERROR_CATEGORY_ORDER = ["proceso_operativo", "practicas_seguras"]

const getErrorStepsData = (arrayData = [], start, end) => {
    // key: `${evaluationTitle}::${stepName}` -> { evaluationTitle, stepName, userIdsByErrorType: Map<errorType, Set<userId>> }
    const groups = new Map()
    arrayData.forEach((row) => {
        if (!isWithinRange(row.Fecha_y_hora, start, end)) return
        const entries = row.evaluaciones || {}
        Object.values(entries).forEach((entry) => {
            if (getEntryType(entry) !== ENTRY_TYPE.EVALUATION) return
            const sections = Array.isArray(entry.sections) ? entry.sections : []
            sections.forEach((section) => {
                const steps = Array.isArray(section.steps) ? section.steps : []
                steps.forEach((step) => {
                    if (step.result === 3) return
                    const categoryIds = Array.isArray(step.categoryIds) && step.categoryIds.length > 0 ? step.categoryIds : []
                    const errorTypes = categoryIds.length > 0
                        ? categoryIds.map((categoryId) => ERROR_CATEGORY_ID_TO_TYPE[categoryId] || "proceso_operativo")
                        : ["proceso_operativo"]
                    const key = `${entry.title}::${step.stepName}`
                    if (!groups.has(key)) {
                        groups.set(key, { evaluationTitle: entry.title, stepName: step.stepName, userIdsByErrorType: new Map() })
                    }
                    const group = groups.get(key)
                    errorTypes.forEach((errorType) => {
                        if (!group.userIdsByErrorType.has(errorType)) group.userIdsByErrorType.set(errorType, new Set())
                        group.userIdsByErrorType.get(errorType).add(row._idUser)
                    })
                })
            })
        })
    })
    return Array.from(groups.values())
        .map(({ evaluationTitle, stepName, userIdsByErrorType }) => {
            const errorTypes = ERROR_CATEGORY_ORDER
                .filter((type) => userIdsByErrorType.has(type))
                .map((type) => ({ type, users: userIdsByErrorType.get(type).size }))
            const allUserIds = new Set()
            userIdsByErrorType.forEach((userIds) => userIds.forEach((id) => allUserIds.add(id)))
            return {
                id: `${evaluationTitle}::${stepName}`,
                stepName,
                evaluation: getEvaluationLabelByTitle(evaluationTitle),
                errorTypes,
                users: allUserIds.size,
            }
        })
        .sort((a, b) => b.users - a.users)
}

const CalendarIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="#001E60" strokeWidth="1.3" />
        <path d="M2 6.5H14" stroke="#001E60" strokeWidth="1.3" />
        <path d="M5 1.5V4" stroke="#001E60" strokeWidth="1.3" strokeLinecap="round" />
        <path d="M11 1.5V4" stroke="#001E60" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
)

const CalendarMonth = ({ monthDate, rangeStart, rangeEnd, canGoPrev, canGoNext, onPrev, onNext, onDayClick }) => {
    const firstOfMonth = startOfMonth(monthDate)
    const leadingBlanks = (firstOfMonth.getDay() + 6) % 7
    const daysInMonth = endOfMonth(monthDate).getDate()

    return (
        <div className='w-[220px]'>
            <div className='flex items-center justify-between mb-2'>
                <button
                    type='button'
                    disabled={!canGoPrev}
                    onClick={(e) => { e.stopPropagation(); onPrev() }}
                    className={`w-6 h-6 flex items-center justify-center rounded text-sm ${canGoPrev ? "text-[#0028A1] hover:bg-[#F5F7FF]" : "text-[#D1D5DB] cursor-default"}`}
                >
                    ‹
                </button>
                <span className='text-sm font-semibold text-[#111827] capitalize'>{MESES[monthDate.getMonth()]} {monthDate.getFullYear()}</span>
                <button
                    type='button'
                    disabled={!canGoNext}
                    onClick={(e) => { e.stopPropagation(); onNext() }}
                    className={`w-6 h-6 flex items-center justify-center rounded text-sm ${canGoNext ? "text-[#0028A1] hover:bg-[#F5F7FF]" : "text-[#D1D5DB] cursor-default"}`}
                >
                    ›
                </button>
            </div>
            <div className='grid grid-cols-7 gap-y-1'>
                {DIAS_SEMANA.map(d => (
                    <span key={d} className='text-[11px] text-[#9A9A9A] text-center'>{d}</span>
                ))}
                {Array.from({ length: leadingBlanks }).map((_, i) => (
                    <span key={`blank-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1
                    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day)
                    const isStart = rangeStart && date.getTime() === rangeStart.getTime()
                    const isEnd = rangeEnd && date.getTime() === rangeEnd.getTime()
                    const isInRange = rangeStart && rangeEnd && date > rangeStart && date < rangeEnd
                    const isEdge = isStart || isEnd
                    return (
                        <button
                            key={day}
                            type='button'
                            onClick={(e) => { e.stopPropagation(); onDayClick(date) }}
                            className={`w-7 h-7 text-xs rounded-full flex items-center justify-center mx-auto
                                ${isEdge ? "bg-[#0028A1] text-white font-semibold" : isInRange ? "bg-[#E7EDFF] text-[#0028A1]" : "text-[#111827] hover:bg-[#F5F7FF]"}`}
                        >
                            {day}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

const DateRangeSelect = ({ onChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [period, setPeriod] = useState("hoy")
    const [range, setRange] = useState(() => {
        const [start, end] = PERIOD_OPTIONS[0].range()
        return { start, end }
    })
    const [leftMonth, setLeftMonth] = useState(() => addMonths(startOfMonth(new Date()), -1))
    const [rightMonth, setRightMonth] = useState(() => startOfMonth(new Date()))
    const pendingStartRef = useRef(null)
    const containerRef = useRef(null)

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false)
        }
        document.addEventListener("mousedown", handleOutsideClick)
        return () => document.removeEventListener("mousedown", handleOutsideClick)
    }, [])

    useEffect(() => {
        onChange?.({ startAt: formatDateParam(range.start), endAt: formatDateParam(range.end), startDate: range.start, endDate: range.end })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range])

    const applyPeriod = (key) => {
        const option = PERIOD_OPTIONS.find(o => o.key === key)
        const [start, end] = option.range()
        setPeriod(key)
        setRange({ start, end })
        setRightMonth(startOfMonth(end))
        setLeftMonth(startOfMonth(end).getTime() === startOfMonth(start).getTime() ? addMonths(startOfMonth(end), -1) : startOfMonth(start))
        pendingStartRef.current = null
    }

    const handleDayClick = (date) => {
        if (!pendingStartRef.current) {
            pendingStartRef.current = date
            setRange({ start: date, end: date })
        } else {
            const start = date < pendingStartRef.current ? date : pendingStartRef.current
            const end = date < pendingStartRef.current ? pendingStartRef.current : date
            setRange({ start, end })
            pendingStartRef.current = null
        }
        setPeriod(null)
    }

    const shiftMonth = (side, delta) => {
        if (side === "left") {
            setLeftMonth(prev => {
                const next = addMonths(prev, delta)
                if (next.getTime() >= startOfMonth(rightMonth).getTime()) setRightMonth(addMonths(next, 1))
                return next
            })
        } else {
            setRightMonth(prev => {
                const next = addMonths(prev, delta)
                if (next.getTime() <= startOfMonth(leftMonth).getTime()) setLeftMonth(addMonths(next, -1))
                return next
            })
        }
    }

    const activeLabel = period ? PERIOD_OPTIONS.find(o => o.key === period)?.label : "Personalizado"

    return (
        <div className='relative' ref={containerRef}>
            <button
                type='button'
                onClick={() => setIsOpen(open => !open)}
                className='flex items-center gap-2.5 border-solid border-[1px] border-[#E7EDFF] rounded-xl px-4 py-2.5 bg-white text-sm'
            >
                <span className='text-[#0028A1] font-medium'>{activeLabel}</span>
                <span className='text-[#E7EDFF]'>|</span>
                <CalendarIcon />
                <span className='text-[#111827]'>{formatRangeLabel(range.start, range.end)}</span>
            </button>

            {isOpen && (
                <div className='absolute left-0 mt-2 bg-white border-solid border-[1px] border-[#E7EDFF] rounded-xl shadow-lg z-20 flex overflow-hidden'>
                    <div className='flex flex-col gap-1 p-2 border-0 border-r border-solid border-[#E7EDFF] min-w-[140px]'>
                        {PERIOD_OPTIONS.map(option => (
                            <button
                                key={option.key}
                                type='button'
                                onClick={() => applyPeriod(option.key)}
                                className={`text-sm text-left rounded-lg px-3 py-2 whitespace-nowrap ${period === option.key ? "bg-[#0D46F2] text-white font-medium" : "text-[#0028A1] hover:bg-[#F5F7FF]"}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <div className='flex gap-4 p-4'>
                        <CalendarMonth
                            monthDate={leftMonth}
                            rangeStart={range.start}
                            rangeEnd={range.end}
                            canGoPrev={true}
                            canGoNext={leftMonth.getTime() < startOfMonth(rightMonth).getTime()}
                            onPrev={() => shiftMonth("left", -1)}
                            onNext={() => shiftMonth("left", 1)}
                            onDayClick={handleDayClick}
                        />
                        <CalendarMonth
                            monthDate={rightMonth}
                            rangeStart={range.start}
                            rangeEnd={range.end}
                            canGoPrev={rightMonth.getTime() > startOfMonth(leftMonth).getTime()}
                            canGoNext={true}
                            onPrev={() => shiftMonth("right", -1)}
                            onNext={() => shiftMonth("right", 1)}
                            onDayClick={handleDayClick}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

const Header = ({ onDateRangeChange }) => (
    <div className='w-full px-6 pt-6 pb-4'>
        <div className='flex justify-between'>
            <p className='text-[#111827] font-bold text-2xl leading-7 mb-4'>Datos de Capacitación</p>
            <p className='text-[#00144F] text-sm'>Planta MXO</p>
        </div>
        <div>
            <p className='text-xs text-[#9A9A9A]'>Período</p>
            <DateRangeSelect onChange={onDateRangeChange} />
        </div>
    </div>
)

const SectionTitle = ({ title = "" }) => (
    <div className='w-full px-6 flex items-center gap-4'>
        <p className='text-[#111827] font-bold text-lg leading-5 whitespace-nowrap'>{title}</p>
        <div className='flex-1 h-[2px] bg-[#E7EDFF]' />
    </div>
)

const SummaryCard = ({ title, value, icon, comparisonText = "Respecto a la semana anterior", comparisonValue }) => (
    <div className='border-solid border-[1px] border-[#E7EDFF] rounded-[14px] bg-[#FFFFFF] p-4 w-[280px]'>
        <div className='flex items-center justify-between'>
            <p className='text-[#6B7280] text-sm'>{title}</p>
            <div className='w-5 h-5'>
                <img src={icon} className='w-full h-full object-contain' alt="" />
            </div>
        </div>
        <div>
            <p className='text-[#111827] font-bold text-[36px] leading-tight mt-2'>{value}</p>
            <div className='flex items-center justify-between gap-2 mt-2'>
                <p className='text-[#00144F] text-xs'>{comparisonText}</p>
                <div className='flex items-center gap-1 bg-[#55AE7033] rounded px-1 py-0.5'>
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 6H1L3.82353 2L7 6Z" fill="#55AE70" />
                    </svg>
                    <p className='text-[#55AE70] text-xs'>{comparisonValue}</p>
                </div>
            </div>
        </div>
    </div>
)

const EvaluationBadge = ({ text }) => (
    <span className='text-xs font-medium px-2 py-1 rounded-full bg-[#E7EDFF] text-[#0028A1] whitespace-nowrap'>{text}</span>
)

const EvaluationSummaryCard = ({ title, badge, value, icon = icons.usersblack, footerLabel = "Usuarios totales" }) => (
    <div className='border-solid border-[1px] border-[#E7EDFF] rounded-[14px] bg-[#FFFFFF] p-4 w-[320px]'>
        <div className='flex items-center justify-between gap-2'>
            <p className='text-[#6B7280] text-sm'>{title}</p>
            <EvaluationBadge text={badge} />
        </div>
        <div>
            <p className='text-[#111827] font-bold text-[36px] leading-tight mt-2'>{value}</p>
            <div className='flex items-center justify-between mt-2'>
                <p className='text-[#00144F] text-xs'>{footerLabel}</p>
                <div className='w-5 h-5'>
                    <img src={icon} className='w-full h-full object-contain' alt="" />
                </div>
            </div>
        </div>
    </div>
)

const lessonsData = [
    { id: "putaway", label: "Proceso: Putaway", value: 250 },
    { id: "devoluciones", label: "Proceso: Devoluciones", value: 220 },
    { id: "receiving", label: "Proceso: Receiving", value: 220 },
    { id: "picking_ph", label: "Proceso: Picking | PH", value: 200 },
    { id: "picking_mhp", label: "Proceso: Picking | MHP", value: 180 },
    { id: "inventario_ciclico", label: "Proceso: Inventario cíclico", value: 160 },
    { id: "colocacion_rack", label: "3/3 | Colocación en rack", value: 120 },
    { id: "picking_mhs", label: "Proceso: Picking | MHS", value: 70 },
    { id: "hu_management", label: "HU Managment, QM & Batch Management", value: 40 },
    { id: "epp", label: "Equipo de protección personal", value: 30 },
]

const barColorByIndex = (index, total) => {
    if (index < Math.ceil(total * 0.4)) return "#0D46F2"
    if (index < Math.ceil(total * 0.8)) return "#648BFF"
    return "#BBCCFF"
}

const getAxisTicks = (data) => {
    const highestValue = data.reduce((max, item) => Math.max(max, item.value), 0)
    const step = Math.ceil(highestValue / 5)
    return Array.from({ length: 6 }, (_, index) => step * index)
}

const LessonsBarChart = ({ data = lessonsData }) => {
    const axisTicks = getAxisTicks(data)
    const maxValue = axisTicks[axisTicks.length - 1] || 1

    return (
        <div className='w-full h-[344px] flex flex-col justify-between pr-10'>
            <div className='flex-1 flex flex-col justify-between mb-4'>
                {data.map((lesson, index) => (
                    <div key={`${index}-${lesson.id}`} className='grid grid-cols-[190px_1fr] items-center gap-3'>
                        <p className='text-xs text-[#282828] text-right truncate'>{lesson.label}</p>
                        <div className='relative h-[18px]'>
                            <div
                                className='h-full rounded-r'
                                style={{
                                    width: `${(lesson.value / maxValue) * 100}%`,
                                    backgroundColor: barColorByIndex(index, data.length),
                                }}
                            />
                            <span className='absolute top-1/2 -translate-y-1/2 text-xs font-semibold text-[#111827]' style={{ left: `calc(${(lesson.value / maxValue) * 100}% + 8px)` }}>
                                {lesson.value}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            <div className='grid grid-cols-[190px_1fr] items-center gap-3 pt-2 border-0 border-t border-solid border-[#E7EDFF]'>
                <div />
                <div className='flex justify-between'>
                    {axisTicks.map((tick, index) => (
                        <span key={`${index}-${tick}`} className='text-xs text-[#9A9A9A]'>{tick}</span>
                    ))}
                </div>
            </div>
        </div>
    )
}

const LegendDot = ({ color }) => (
    <span className='w-2 h-2 rounded-full inline-block' style={{ backgroundColor: color }} />
)

const LessonsChartCard = ({ mostCursada, menosCursada, totalLecciones, data }) => (
    <div className='rounded-2xl border-solid border-[1px] border-[#E7EDFF] bg-white overflow-hidden'>
        <div className='pt-6 px-[23px] pb-4 border-0 border-b border-solid border-[#E7EDFF]'>
            <div className='flex items-start justify-between'>
                <div>
                    <p className='text-[#111827] font-bold text-base'>Temas más cursadas</p>
                    <p className='text-[#9A9A9A] text-xs mt-1'>Cantidad de usuarios que han cursado cada tema</p>
                </div>
                <div className='flex items-center gap-4 mt-1'>
                    <span className='flex items-center gap-1.5 text-xs text-[#282828]'>
                        <LegendDot color='#0D46F2' /> Más cursada
                    </span>
                    <span className='flex items-center gap-1.5 text-xs text-[#282828]'>
                        <LegendDot color='#648BFF' /> Promedio
                    </span>
                    <span className='flex items-center gap-1.5 text-xs text-[#282828]'>
                        <LegendDot color='#BBCCFF' /> Menos cursada
                    </span>
                </div>
            </div>
        </div>
        <div className='px-[23px] pt-4 pb-4'>
            <LessonsBarChart data={data} />
        </div>
        <div className='bg-[#F2F2F2] px-[23px] py-3 grid grid-cols-3'>
            <p className='text-xs text-[#282828] text-center flex items-center justify-center gap-1.5'>
                <LegendDot color='#0D46F2' />
                <span className='font-semibold'>Más cursada:</span> {mostCursada}
            </p>
            <p className='text-xs text-[#282828] text-center flex items-center justify-center gap-1.5 border-0 border-l border-solid border-[#CCCCCC]'>
                <LegendDot color='#BBCCFF' />
                <span className='font-semibold'>Menos cursada:</span> {menosCursada}
            </p>
            <p className='text-xs text-[#282828] text-center flex items-center justify-center gap-1.5 border-0 border-l border-solid border-[#CCCCCC]'>
                <span className='font-semibold'>Total de lecciones:</span> {totalLecciones}
            </p>
        </div>
    </div>
)

const ChartPlaceholderCard = ({ title, subtitle, children }) => (
    <div className='rounded-2xl border-solid border-[1px] border-[#E7EDFF] bg-white overflow-hidden flex-1 min-w-[320px]'>
        <div className='pt-6 px-[23px] pb-4 border-0 border-b border-solid border-[#E7EDFF]'>
            <p className='text-[#00144F] font-bold text-base'>{title}</p>
            <p className='text-[#808080] text-xs mt-1'>{subtitle}</p>
        </div>
        <div className='px-[23px] pt-4 pb-4'>
            {children || <div className='w-full h-[300px] bg-red-500' />}
        </div>
    </div>
)

const approvalRateData = [
    { key: "hs_receiving", label: "HS | Receiving", totalUsers: 90, completedUsers: 90 },
    { key: "hp_putaway", label: "HP | Putaway", totalUsers: 40, completedUsers: 12 },
    { key: "ph_picking", label: "PH | Picking", totalUsers: 18, completedUsers: 13 },
]

const getApprovalRate = (completedUsers, totalUsers) => (
    totalUsers > 0 ? Math.round((completedUsers / totalUsers) * 100) : 0
)

const getApprovalColor = (rate) => {
    if (rate >= 75) return "#3CCB7F"
    if (rate >= 40) return "#0D46F2"
    return "#FFC24B"
}

const APPROVAL_AXIS_TICKS = [100, 75, 50, 25, 0]

const ApprovalRateBarChart = ({ data = approvalRateData }) => (
    <div className='w-full h-[300px] flex pb-10'>
        <div className='flex flex-col justify-between h-full pb-6 pr-3'>
            {APPROVAL_AXIS_TICKS.map(tick => (
                <span key={tick} className='text-xs text-[#9A9A9A]'>{tick}%</span>
            ))}
        </div>
        <div className='flex-1 relative border-0 border-l border-solid border-[#E7EDFF]'>
            <div className='absolute inset-0 pb-6 flex flex-col justify-between pointer-events-none'>
                {APPROVAL_AXIS_TICKS.map(tick => (
                    <div key={tick} className='w-full border-0 border-t border-dashed border-[#E7EDFF]' />
                ))}
            </div>
            <div className='relative h-full grid grid-flow-col auto-cols-fr items-end pb-6'>
                {data.map(item => {
                    const rate = getApprovalRate(item.completedUsers, item.totalUsers)
                    const clampedRate = Math.min(100, Math.max(0, rate))
                    const color = getApprovalColor(clampedRate)
                    return (
                        <div key={item.key} className='relative h-full flex flex-col justify-end items-center group'>
                            <div className='absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white shadow-lg rounded-lg px-3 py-2 whitespace-nowrap z-10 pointer-events-none'>
                                <p className='text-xs text-[#111827]'>{item.completedUsers}/{item.totalUsers} usuarios</p>
                                <p className='text-xs font-semibold text-[#0028A1]'>Aprobación: {rate}%</p>
                            </div>
                            <div
                                className='w-16 rounded-t-md transition-all'
                                style={{ height: `${clampedRate}%`, backgroundColor: color }}
                            />
                        </div>
                    )
                })}
            </div>
            <div className='grid grid-flow-col auto-cols-fr'>
                {data.map(item => {
                    const rate = getApprovalRate(item.completedUsers, item.totalUsers)
                    const color = getApprovalColor(Math.min(100, Math.max(0, rate)))
                    return (
                        <div key={item.key} className='text-center'>
                            <p className='text-xs font-semibold' style={{ color }}>{item.label}</p>
                            <p className='text-xs text-[#9A9A9A]'>{item.completedUsers}/{item.totalUsers}</p>
                        </div>
                    )
                })}
            </div>
        </div>
    </div>
)

// Unidad adaptativa: segundos si no llega al minuto, minutos si no llega a la hora,
// horas si llega o pasa de la hora — mostrar "0 h" con segundos de prueba no sirve.
const formatDuration = (totalSeconds) => {
    const seconds = Math.round(totalSeconds || 0)
    if (seconds < 60) return `${seconds} seg`
    if (seconds < 3600) return `${Math.round(seconds / 60)} min`
    return `${Math.round(seconds / 3600)} h`
}

const timeInvestedData = [
    { key: "hs_receiving", label: "HS | Receiving", dateRange: "21/08/26 - 21/09/26", seconds: 180 * 3600 },
    { key: "hp_putaway", label: "HP | Putaway", dateRange: "21/08/26 - 21/09/26", seconds: 90 * 3600 },
    { key: "ph_picking", label: "PH | Picking", dateRange: "21/08/26 - 21/09/26", seconds: 45 * 3600 },
]

const TimeInvestedChart = ({ data = timeInvestedData }) => {
    const maxSeconds = data.reduce((max, item) => Math.max(max, item.seconds), 0) || 1

    return (
        <div className='w-full flex flex-col gap-6'>
            {data.map(item => {
                const widthPercent = Math.min(100, Math.max(0, (item.seconds / maxSeconds) * 100))
                return (
                    <div key={item.key}>
                        <div className='flex justify-end'>
                            <p className='text-lg font-bold text-[#111827]'>{formatDuration(item.seconds)}</p>
                        </div>
                        <div className='grid grid-cols-[160px_1fr] items-center gap-3'>
                            <div>
                                <p className='flex items-center gap-1.5 text-sm text-[#111827]'>
                                    <LegendDot color='#0D46F2' /> {item.label}
                                </p>
                                <p className='text-xs text-[#9A9A9A] mt-1'>{item.dateRange}</p>
                            </div>
                            <div className='h-2.5 rounded-full bg-[#E7EDFF] overflow-hidden'>
                                <div
                                    className='h-full rounded-full bg-[#0D46F2]'
                                    style={{ width: `${widthPercent}%` }}
                                />
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

const ERROR_TYPE_STYLES = {
    proceso_operativo: { bg: "#FFF4C1", text: "#6A5905", label: "Proceso operativo" },
    practicas_seguras: { bg: "#EDE9FE", text: "#7C3AED", label: "Prácticas seguras" },
}

const ErrorTypeBadge = ({ type }) => {
    const style = ERROR_TYPE_STYLES[type]
    return (
        <span
            className='text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap'
            style={{ backgroundColor: style.bg, color: style.text }}
        >
            {style.label}
        </span>
    )
}

const EVALUATION_OPTIONS = ["HS | Receiving", "HP | Putaway", "PH | Picking"]

const errorStepsData = [
    { id: 1, stepName: "Selección de puerta (por revisión de Documento)", evaluation: "HS | Receiving", errorTypes: [{ type: "proceso_operativo", users: 33 }, { type: "practicas_seguras", users: 33 }], users: 33 },
    { id: 2, stepName: "Selección de puerta (por revisión de Documento)", evaluation: "HP | Putaway", errorTypes: [{ type: "proceso_operativo", users: 21 }, { type: "practicas_seguras", users: 21 }], users: 21 },
    { id: 3, stepName: "Selección de puerta (por revisión de Documento)", evaluation: "PH | Picking", errorTypes: [{ type: "proceso_operativo", users: 12 }, { type: "practicas_seguras", users: 12 }], users: 12 },
]

const ChevronIcon = ({ isOpen }) => (
    <svg
        width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"
        className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
    >
        <path d="M1 1.5L6 6.5L11 1.5" stroke="#0028A1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const EvaluationSelect = ({ value, onChange, options = EVALUATION_OPTIONS }) => {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <div className='relative'>
            <button
                type='button'
                onClick={() => setIsOpen(open => !open)}
                className='flex items-center gap-3 border-solid border-[1px] border-[#E7EDFF] rounded-xl px-4 py-2.5 bg-white text-[#0028A1] font-medium text-sm min-w-[170px] justify-between'
            >
                {value}
                <ChevronIcon isOpen={isOpen} />
            </button>
            {isOpen && (
                <div className='absolute right-0 mt-2 w-full bg-white border-solid border-[1px] border-[#E7EDFF] rounded-xl shadow-lg p-1.5 flex flex-col gap-1 z-20'>
                    {options.map(option => {
                        const isSelected = option === value
                        return (
                            <button
                                key={option}
                                type='button'
                                onClick={() => { onChange(option); setIsOpen(false) }}
                                className={`text-sm font-medium rounded-lg px-4 py-2 text-center ${isSelected ? "bg-[#0D46F2] text-white" : "text-[#0028A1] hover:bg-[#F5F7FF]"}`}
                            >
                                {option}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

const ErrorRecurrenceCard = ({ data = errorStepsData, evaluationOptions = EVALUATION_OPTIONS }) => {
    const [selectedEvaluation, setSelectedEvaluation] = useState(evaluationOptions[0])
    const filteredData = data.filter(item => item.evaluation === selectedEvaluation)

    return (
        <div className='rounded-2xl border-solid border-[1px] border-[#E7EDFF] bg-white overflow-hidden'>
            <div className='pt-6 px-6 pb-4 flex items-start justify-between gap-4'>
                <div>
                    <p className='text-[#111827] font-bold text-base'>Pasos con mayor recurrencia de errores</p>
                    <p className='text-[#9A9A9A] text-xs mt-1'>Frecuencia de errores registrados por paso</p>
                </div>
                <EvaluationSelect value={selectedEvaluation} onChange={setSelectedEvaluation} options={evaluationOptions} />
            </div>
            <div className='overflow-x-auto overflow-y-auto max-h-[500px]'>
                <table className='w-full border-collapse'>
                    <thead>
                        <tr className='bg-[#0D46F2] sticky top-0 z-10'>
                            <th className='text-white text-sm font-semibold text-left py-3 pl-6 pr-4 w-16'>#</th>
                            <th className='text-white text-sm font-semibold text-left py-3 px-4'>Nombre del paso</th>
                            <th className='text-white text-sm font-semibold text-left py-3 px-4'>Evaluación</th>
                            <th className='text-white text-sm font-semibold text-left py-3 px-4'>Tipo de error</th>
                            <th className='text-white text-sm font-semibold text-right py-3 pr-6 pl-4'>#Usuarios</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length === 0 ? (
                            <tr>
                                <td colSpan={5} className='py-10 text-center text-sm text-[#9A9A9A]'>Sin pasos</td>
                            </tr>
                        ) : filteredData.map((item, index) => (
                            <tr key={item.id} className='border-0 border-b border-solid border-[#F2F2F2] last:border-b-0'>
                                <td className='py-4 pl-6 pr-4'>
                                    <span
                                        className='inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold'
                                        style={{ backgroundColor: "#FCE7E7", color: "#D92D20" }}
                                    >
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </td>
                                <td className='py-4 px-4 text-sm text-[#111827]'>{item.stepName}</td>
                                <td className='py-4 px-4'><EvaluationBadge text={item.evaluation} /></td>
                                <td className='py-4 px-4'>
                                    <div className='flex flex-col gap-2 items-start'>
                                        {item.errorTypes.map((errorType) => (
                                            <ErrorTypeBadge key={errorType.type} type={errorType.type} />
                                        ))}
                                    </div>
                                </td>
                                <td className='py-4 pr-6 pl-4 text-right text-sm font-bold text-[#111827]'>
                                    <div className='flex flex-col gap-2 items-end'>
                                        {item.errorTypes.map((errorType) => (
                                            <span key={errorType.type}>{errorType.users}</span>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export const TrainingDashboardColgateMontecarga = ({ hideHeader = false }) => {
    const { arrayData } = useSelector(state => state.table)
    const [dateRange, setDateRange] = useState(() => {
        const [start, end] = PERIOD_OPTIONS[0].range()
        return { start, end }
    })

    const handleDateRangeChange = ({ startDate, endDate }) => {
        setDateRange({ start: startDate, end: endDate })
    }

    const totalUsuarios = countUniqueUsersInRange(arrayData, dateRange.start, dateRange.end)

    const [semanaPasadaStart, semanaPasadaEnd] = PERIOD_OPTIONS.find(o => o.key === "semana_pasada").range()
    const usuariosSemanaPasada = countUniqueUsersInRange(arrayData, semanaPasadaStart, semanaPasadaEnd)
    const comparativoTotalUsuarios = totalUsuarios - usuariosSemanaPasada

    const usuariosConLeccion = countUniqueUsersWithLessonCompletedInRange(arrayData, dateRange.start, dateRange.end)
    const usuariosConLeccionSemanaPasada = countUniqueUsersWithLessonCompletedInRange(arrayData, semanaPasadaStart, semanaPasadaEnd)
    const comparativoUsuariosConLeccion = usuariosConLeccion - usuariosConLeccionSemanaPasada

    const usuariosConEvaluacion = countUniqueUsersWithEvaluationCompletedInRange(arrayData, dateRange.start, dateRange.end)
    const usuariosConEvaluacionSemanaPasada = countUniqueUsersWithEvaluationCompletedInRange(arrayData, semanaPasadaStart, semanaPasadaEnd)
    const comparativoUsuariosConEvaluacion = usuariosConEvaluacion - usuariosConEvaluacionSemanaPasada

    const lessonsChartData = getLessonsChartData(arrayData, dateRange.start, dateRange.end)
    const mostCursada = lessonsChartData[0]?.label ?? "-"
    const menosCursada = lessonsChartData[lessonsChartData.length - 1]?.label ?? "-"
    const totalLecciones = `${LESSON_CATALOG.length} lecciones`

    // Orden fijo HS Receiving / HP Putaway / PH Picking, igual que EVALUATION_CATALOG.
    const evaluationSummaryData = getEvaluationSummaryData(arrayData, dateRange.start, dateRange.end)
    const evaluationTimeInvestedData = getEvaluationTimeInvestedData(arrayData, dateRange.start, dateRange.end)
    const errorStepsDataReal = getErrorStepsData(arrayData, dateRange.start, dateRange.end)

    return (
        <div className={`w-full bg-[#F9FAFB] ${hideHeader ? "" : "h-screen overflow-scroll"}`}>
            {hideHeader && (
                <div aria-hidden className='h-0 overflow-hidden' style={{ width: "calc(100vw - 315px)" }} />
            )}
            <Header onDateRangeChange={handleDateRangeChange} />
            <SectionTitle title={"Resumen de usuarios"} />
            <div className='flex flex-wrap gap-6 px-6 pt-4 pb-6'>
                <SummaryCard title={"Total de usuarios"} value={totalUsuarios} comparisonValue={comparativoTotalUsuarios} icon={icons.usersblack} />
                <SummaryCard title={"Con lección completada"} value={usuariosConLeccion} comparisonValue={comparativoUsuariosConLeccion} icon={icons.bookblack} />
                <SummaryCard title={"Con evaluación completada"} value={usuariosConEvaluacion} comparisonValue={comparativoUsuariosConEvaluacion} icon={icons.boardblack} />
            </div>
            <SectionTitle title={"Datos de Temas"} />
            <div className='px-6 pt-4 pb-6'>
                <LessonsChartCard mostCursada={mostCursada} menosCursada={menosCursada} totalLecciones={totalLecciones} data={lessonsChartData} />
            </div>
            <SectionTitle title={"Datos de evaluaciones"} />
            <div className='flex flex-wrap gap-6 px-6 pt-4 pb-6'>
                <EvaluationSummaryCard title={"Montacargas Hombre sentado"} badge={"Receiving"} value={evaluationSummaryData[0].totalUsers} />
                <EvaluationSummaryCard title={"Montacargas Hombre a pie"} badge={"Putaway"} value={evaluationSummaryData[1].totalUsers} />
                <EvaluationSummaryCard title={"Patín hidráulico"} badge={"Picking"} value={evaluationSummaryData[2].totalUsers} />
            </div>
            <div className='flex flex-wrap gap-6 px-6 pb-6'>
                <ChartPlaceholderCard title={"Tasa de aprobación"} subtitle={"Usuarios que completaron y aprobaron cada evaluación"}>
                    <ApprovalRateBarChart data={evaluationSummaryData} />
                </ChartPlaceholderCard>
                <ChartPlaceholderCard title={"Tiempo invertido en evaluaciones"} subtitle={"Total acumulado por evaluación"}>
                    <TimeInvestedChart data={evaluationTimeInvestedData} />
                </ChartPlaceholderCard>
            </div>
            <div className='px-6 pb-6'>
                <ErrorRecurrenceCard data={errorStepsDataReal} evaluationOptions={EVALUATION_CATALOG.map(e => e.label)} />
            </div>
        </div>
    )
}

export default TrainingDashboardColgateMontecarga
